import React, { useState, useEffect, useContext } from 'react';
import { API_BASE_URL } from '../config';
import { UserPlus, Edit, Trash2, Check, X, BarChart, UserCog, Phone, Mail, Key, Shield, ToggleLeft, User, UserCheck, Users, RefreshCw, Building } from 'lucide-react';
import { UsersContext } from '../App'; // Assicurati che il percorso sia corretto


const UserManagement = () => {
  // Ottieni lo stato condiviso dal contesto
  const { usersState, setUsersState } = useContext(UsersContext);
  
  // Mantieni solo gli stati locali necessari
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [show3CXModal, setShow3CXModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [cx3Mappings, setCx3Mappings] = useState([]);
  const [cx3FormData, setCx3FormData] = useState({
    user_id: '',
    cx3_id: '',
    cx3_name: ''
  });

  // Form state
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    is_active: true,
    department: '',
    cx3_id: '',
    cx3_name: ''
  });

  useEffect(() => {
    // Carica gli utenti solo se non sono già stati caricati
    if (usersState.length === 0) {
      fetchUsers();
    } else {
      setLoading(false); // Non serve caricare se abbiamo già i dati
    }
    
    // Carica le associazioni 3CX
    fetch3CXMappings();
    
    // Configura un intervallo per aggiornare periodicamente gli utenti
    const pollingInterval = setInterval(() => {
      console.log('Polling für Benutzeraktualisierungen...');
      fetchUsers(false); // passa false per non impostare lo stato di caricamento
    }, 30000); // Controlla ogni 30 secondi
    
    // Pulisci l'intervallo quando il componente viene smontato
    return () => clearInterval(pollingInterval);
  }, [usersState.length]);

  // Aggiungi un Effect per ascoltare quando la pagina diventa visibile
  useEffect(() => {
    // Funzione che verrà chiamata quando la visibilità della pagina cambia
    const handleVisibilityChange = () => {
      // Se la pagina è diventata visibile, aggiorna i dati
      if (document.visibilityState === 'visible') {
        console.log('Seite wurde sichtbar, Daten werden aktualisiert...');
        fetchUsers(false);
        fetch3CXMappings();
      }
    };

    // Registra l'event listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Pulisci l'event listener quando il componente viene smontato
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchUsers = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Benutzer');
      }

      const data = await response.json();
      console.log('Users fetched from server:', data);
      
      // Confronta i dati del server con quelli locali
      const hasChanges = compareUsersData(usersState, data);
      
      if (hasChanges) {
        // Usa il setter del contesto per aggiornare lo stato
        setUsersState(data);
        setSyncStatus({
          type: 'success',
          message: 'Benutzer erfolgreich synchronisiert! Änderungen wurden erkannt.'
        });
        // Rimuovi il messaggio dopo 5 secondi
        setTimeout(() => setSyncStatus(null), 5000);
      } else {
        setSyncStatus({
          type: 'info',
          message: 'Keine Änderungen erkannt.'
        });
        // Rimuovi il messaggio dopo 3 secondi
        setTimeout(() => setSyncStatus(null), 3000);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      // Mostra errore solo se è un caricamento esplicito, non durante il polling
      if (showLoading) {
        setError(error.message);
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  // Funzione per confrontare gli utenti correnti con quelli nuovi
  const compareUsersData = (currentUsers, newUsers) => {
    // Se il numero di utenti è diverso, ci sono state modifiche
    if (currentUsers.length !== newUsers.length) {
      return true;
    }
    
    // Converti gli array in mappe per facilitare il confronto
    const currentUsersMap = new Map(currentUsers.map(user => [user.id, user]));
    
    // Controlla se ci sono nuovi utenti o modifiche agli utenti esistenti
    for (const newUser of newUsers) {
      const currentUser = currentUsersMap.get(newUser.id);
      
      // Se l'utente non esiste o ha cambiamenti nei campi rilevanti
      if (!currentUser || 
          currentUser.username !== newUser.username ||
          currentUser.email !== newUser.email ||
          currentUser.role !== newUser.role ||
          currentUser.is_active !== newUser.is_active ||
          currentUser.department !== newUser.department) {
        return true;
      }
    }
    
    // Se arriviamo qui, non ci sono modifiche
    return false;
  };

  const fetch3CXMappings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/3cx-mappings/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        console.error('3CX API Error:', response.status, response.statusText);
        console.error('URL chiamata:', `${API_BASE_URL}/api/3cx-mappings/`);
        throw new Error(`Fehler beim Laden der 3CX-Zuordnungen (${response.status}: ${response.statusText})`);
      }

      const data = await response.json();
      setCx3Mappings(data);
    } catch (error) {
      console.error('Fehler:', error);
      setError(error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'user',
      is_active: true,
      department: '',
      cx3_id: '',
      cx3_name: ''
    });
  };

  const handleAddUser = async () => {
    // Inizia con lo stato di caricamento e pulisci gli errori precedenti
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      // Estrai i dati 3CX dal form
      const { cx3_id, cx3_name, ...userData } = formData;
      
      // Assicurati che is_active sia un booleano
      userData.is_active = userData.is_active === true;
      
      console.log('Creating user with data:', userData);
      console.log('is_active value:', userData.is_active, 'type:', typeof userData.is_active);
      
      // Registra l'utente
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });
  
      // Se la risposta non è ok, gestisci l'errore
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error data:', errorData);
        
        // Estrai il messaggio di errore in base alla struttura
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Gestisci array di errori di validazione (formato tipico di FastAPI)
            const errorsMessages = errorData.detail.map(err => {
              // Estrai il nome del campo e il messaggio
              const field = err.loc[err.loc.length - 1];
              // Converti i nomi dei campi in tedesco per una migliore esperienza utente
              const fieldName = {
                'username': 'Benutzername',
                'email': 'E-Mail',
                'password': 'Passwort',
                'role': 'Rolle',
                'is_active': 'Status'
              }[field] || field;
              
              return `${fieldName}: ${err.msg}`;
            }).join('\n');
            
            setError(errorsMessages);
          } else if (typeof errorData.detail === 'string') {
            // Messaggio di errore semplice
            setError(errorData.detail);
          } else {
            // Se detail è un oggetto, convertilo in una stringa leggibile
            setError(
              Object.entries(errorData.detail)
                .map(([key, value]) => `${key}: ${value}`)
                .join('\n')
            );
          }
        } else {
          setError('Ein unbekannter Fehler ist aufgetreten');
        }
      } else {
        // Successo: ottieni i dati dell'utente creato
        const userData = await response.json();
        console.log('User created:', userData);
        
        // Se è stato specificato un ID 3CX, crea l'associazione
        if (cx3_id) {
          try {
            const cx3Response = await fetch(`${API_BASE_URL}/api/3cx-mappings/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                user_id: userData.id,
                cx3_id: cx3_id,
                cx3_name: cx3_name
              })
            });
            
            if (!cx3Response.ok) {
              console.error('Error creating 3CX mapping:', await cx3Response.text());
            }
          } catch (cx3Error) {
            console.error('Error creating 3CX mapping:', cx3Error);
          }
        }
        
        // Aggiorna la lista degli utenti e chiudi il modale
        await fetchUsers();
        await fetch3CXMappings();
        setShowAddModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError('Verbindungsfehler: Bitte überprüfen Sie Ihre Internetverbindung');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditUser = (user) => {
    setSelectedUser(user);
    
    // Cerca se esiste già un'associazione 3CX per questo utente
    const existingMapping = cx3Mappings.find(mapping => mapping.user_id === user.id);
    
    // Assicurati che is_active sia un booleano
    const isActive = typeof user.is_active === 'boolean' ? user.is_active : user.is_active === true || user.is_active === 'true';
    
    console.log('Editing user:', user);
    console.log('Original is_active:', user.is_active, 'type:', typeof user.is_active);
    console.log('Converted is_active:', isActive, 'type:', typeof isActive);
    
    setFormData({
      username: user.username,
      email: user.email || '',
      role: user.role,
      is_active: isActive,
      department: user.department || '',
      // Aggiungi campi per la password e l'associazione 3CX
      password: '',
      cx3_id: existingMapping ? existingMapping.cx3_id : '',
      cx3_name: existingMapping ? existingMapping.cx3_name || '' : ''
    });
    
    setShowEditModal(true);
  };

  const handleUpdateUser = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Verifica se lo stato attivo è cambiato
      const isActiveChanged = selectedUser.is_active !== formData.is_active;
      
      // Aggiorna le informazioni dell'utente
      const userUpdateData = {
        role: formData.role,
        department: formData.department
      };
      
      // Aggiungi la password solo se è stata inserita
      if (formData.password) {
        userUpdateData.password = formData.password;
      }
      
      // Log di debug
      console.log('Updating user with data:', userUpdateData);
      console.log('is_active changed:', isActiveChanged);
      console.log('Original is_active:', selectedUser.is_active);
      console.log('New is_active:', formData.is_active);
      
      // Aggiorna le informazioni dell'utente (escluso is_active)
      const userResponse = await fetch(`${API_BASE_URL}/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userUpdateData)
      });

      if (!userResponse.ok) {
        const errorData = await userResponse.json();
        throw new Error(errorData.detail || 'Fehler beim Aktualisieren des Benutzers');
      }
      
      // Se lo stato attivo è cambiato, usa l'endpoint toggle-active
      if (isActiveChanged) {
        // Esegui il toggle solo se necessario (se lo stato attuale non corrisponde a quello desiderato)
        const toggleResponse = await fetch(`${API_BASE_URL}/users/${selectedUser.id}/toggle-active`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!toggleResponse.ok) {
          const errorData = await toggleResponse.json();
          throw new Error(errorData.detail || 'Fehler beim Ändern des Aktivitätsstatus');
        }
        
        // Ottieni l'utente aggiornato dalla risposta
        const toggledUser = await toggleResponse.json();
        console.log('Toggled user:', toggledUser);
        
        // Verifica se lo stato finale corrisponde a quello desiderato
        if (toggledUser.is_active !== formData.is_active) {
          // Se non corrisponde, esegui un altro toggle
          const secondToggleResponse = await fetch(`${API_BASE_URL}/users/${selectedUser.id}/toggle-active`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!secondToggleResponse.ok) {
            throw new Error('Fehler beim Ändern des Aktivitätsstatus');
          }
        }
      }
      
      // Gestisci l'associazione 3CX
      if (formData.cx3_id) {
        // Crea o aggiorna l'associazione 3CX
        const cx3Response = await fetch(`${API_BASE_URL}/api/3cx-mappings/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            user_id: selectedUser.id,
            cx3_id: formData.cx3_id,
            cx3_name: formData.cx3_name
          })
        });

        if (!cx3Response.ok) {
          throw new Error('Fehler beim Speichern der 3CX-Zuordnung');
        }
      } else {
        // Se l'ID 3CX è vuoto ma esisteva prima, elimina l'associazione
        const existingMapping = cx3Mappings.find(mapping => mapping.user_id === selectedUser.id);
        if (existingMapping) {
          const deleteResponse = await fetch(`${API_BASE_URL}/api/3cx-mappings/${selectedUser.id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (!deleteResponse.ok) {
            throw new Error('Fehler beim Löschen der 3CX-Zuordnung');
          }
        }
      }

      // Aggiorna i dati
      await fetchUsers();
      await fetch3CXMappings();
      setShowEditModal(false);
    } catch (error) {
      setError(error.message);
      console.error('Error updating user:', error);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${user.id}/toggle-active`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Ändern des Aktivitätsstatus');
      }

      // Ottieni l'utente aggiornato dalla risposta
      const updatedUser = await response.json();
      console.log('Updated user:', updatedUser);
      
      // Aggiorna lo stato condiviso
      setUsersState(prevUsers => prevUsers.map(u => 
        u.id === updatedUser.id ? { ...u, is_active: updatedUser.is_active } : u
      ));
    } catch (error) {
      setError(error.message);
      console.error('Error toggling user active state:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    // Trova l'utente e imposta come selezionato
    const userToDelete = usersState.find(user => user.id === userId);
    setSelectedUser(userToDelete);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Fehler beim Löschen des Benutzers');
      }

      // Chiudi il modale
      setShowDeleteConfirmModal(false);
      
      // Aggiorna la lista degli utenti
      await fetchUsers();
      
      // Mostra un messaggio di successo
      setError(`Der Benutzer "${selectedUser.username}" wurde erfolgreich gelöscht.`);
      
      // Rimuovi il messaggio dopo 5 secondi
      setTimeout(() => {
        setError(null);
      }, 5000);
    } catch (error) {
      setError(error.message);
      console.error('Error deleting user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/users/${userId}/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Benutzerstatistiken');
      }

      const stats = await response.json();
      setUserStats(stats);
      setShowStatsModal(true);
    } catch (error) {
      setError(error.message);
      console.error('Error fetching user stats:', error);
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'agent': return 'Agent';
      case 'jk_manager': return 'JK Manager';
      case 'user': return 'Benutzer';
      default: return role;
    }
  };

  const handle3CXInputChange = (e) => {
    const { name, value } = e.target;
    setCx3FormData({
      ...cx3FormData,
      [name]: value
    });
  };

  const handleManage3CX = (user) => {
    setSelectedUser(user);
    
    // Cerca se esiste già un'associazione per questo utente
    const existingMapping = cx3Mappings.find(mapping => mapping.user_id === user.id);
    
    if (existingMapping) {
      setCx3FormData({
        user_id: existingMapping.user_id,
        cx3_id: existingMapping.cx3_id,
        cx3_name: existingMapping.cx3_name || ''
      });
    } else {
      setCx3FormData({
        user_id: user.id,
        cx3_id: '',
        cx3_name: ''
      });
    }
    
    setShow3CXModal(true);
  };

  const handleSave3CXMapping = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/3cx-mappings/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(cx3FormData)
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern der 3CX-Zuordnung');
      }

      // Aggiorna la lista delle associazioni
      fetch3CXMappings();
      
      // Chiudi il modal
      setShow3CXModal(false);
    } catch (error) {
      console.error('Fehler:', error);
      setError(error.message);
    }
  };

  const handleDelete3CXMapping = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/3cx-mappings/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Löschen der 3CX-Zuordnung');
      }

      // Aggiorna la lista delle associazioni
      fetch3CXMappings();
    } catch (error) {
      console.error('Fehler:', error);
      setError(error.message);
    }
  };

  return (
    <div>
      <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-xl font-semibold flex items-center">
          <Users className="mr-2 h-5 w-5 text-indigo-500" />
          Benutzerverwaltung
        </h1>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => {
              fetchUsers();
              fetch3CXMappings();
            }}
            className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded flex items-center justify-center"
            title="Daten aktualisieren"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded flex items-center w-full sm:w-auto justify-center"
          >
            <UserPlus className="mr-2 h-5 w-5" />
            <span>Benutzer hinzufügen</span>
          </button>
        </div>
      </div>

      <div className="p-6">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {syncStatus && (
        <div className={`px-4 py-3 rounded mb-4 ${
          syncStatus.type === 'success' 
            ? 'bg-green-100 border border-green-400 text-green-700' 
            : 'bg-blue-100 border border-blue-400 text-blue-700'
        }`}>
          {syncStatus.message}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">Wird geladen...</div>
      ) : (
        <>
          {/* Tabella per desktop (nascosta su mobile) */}
          <div className="hidden md:block bg-white shadow-md rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Benutzername
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rolle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Abteilung
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    3CX-Operator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usersState.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 
                        user.role === 'jk_manager' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.department ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {user.department || 'Keine Abteilung'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {cx3Mappings.find(mapping => mapping.user_id === user.id) ? (
                        <div className="text-sm text-gray-900">
                          ID: {cx3Mappings.find(mapping => mapping.user_id === user.id).cx3_id}
                          {cx3Mappings.find(mapping => mapping.user_id === user.id).cx3_name && (
                            <span className="ml-1 text-gray-500">
                              ({cx3Mappings.find(mapping => mapping.user_id === user.id).cx3_name})
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">Nicht zugeordnet</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Bearbeiten"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className={`${
                            user.is_active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                          }`}
                          title={user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                        >
                          {user.is_active ? <X className="h-5 w-5" /> : <Check className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Löschen"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => fetchUserStats(user.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Statistiken"
                        >
                          <BarChart className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Card layout per mobile (nascosto su desktop) */}
          <div className="md:hidden space-y-4">
            {usersState.map((user) => (
              <div key={user.id} className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <div className="font-medium text-gray-900 flex items-center">
                    <Shield className="h-4 w-4 mr-2 text-gray-400" />
                    Rolle:
                  </div>
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 
                    user.role === 'jk_manager' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {getRoleLabel(user.role)}
                  </span>
                </div>
                <div className="px-4 py-3 space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500 flex items-center">
                      <ToggleLeft className="h-4 w-4 mr-2 text-gray-400" />
                      Status:
                    </div>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500 flex items-center">
                      <Building className="h-4 w-4 mr-2 text-gray-400" />
                      Abteilung:
                    </div>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.department ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.department || 'Keine Abteilung'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500 flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-gray-400" />
                      3CX:
                    </div>
                    <div className="text-sm text-right">
                      {cx3Mappings.find(mapping => mapping.user_id === user.id) ? (
                        <div className="text-gray-900">
                          ID: {cx3Mappings.find(mapping => mapping.user_id === user.id).cx3_id}
                          {cx3Mappings.find(mapping => mapping.user_id === user.id).cx3_name && (
                            <div className="text-gray-500 text-xs">
                              {cx3Mappings.find(mapping => mapping.user_id === user.id).cx3_name}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-gray-500">Nicht zugeordnet</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-between">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
                      title="Bearbeiten"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => fetchUserStats(user.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
                      title="Statistiken"
                    >
                      <BarChart className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleToggleActive(user)}
                      className={`w-8 h-8 flex items-center justify-center rounded-full ${
                        user.is_active 
                          ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                          : 'bg-green-100 text-green-600 hover:bg-green-200'
                      }`}
                      title={user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {user.is_active ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                      title="Löschen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Add User Modal with Improved Error Handling */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 max-w-2xl w-full my-8 relative">
            <div className="sticky top-0 bg-white z-10 pb-2">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <UserPlus className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-indigo-600" />
                  Neuen Benutzer erstellen
                </h3>
                <button
                  onClick={() => {
                    setError(''); // Reset error when closing
                    setShowAddModal(false);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
              
              {/* Visualizzazione degli errori */}
              {error && (
                <div className="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-500 p-3 sm:p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Fehler bei der Registrierung</h3>
                      <div className="mt-2 text-sm text-red-700">
                        {typeof error === 'string' ? (
                          <p>{error}</p>
                        ) : (
                          <ul className="list-disc pl-5 space-y-1">
                            {Object.entries(error).map(([key, value]) => (
                              <li key={key}>{value}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              {/* Informazioni utente */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700 border-b pb-2 flex items-center">
                  <User className="h-5 w-5 mr-2 text-indigo-500" />
                  Benutzerinformationen
                </h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <UserCheck className="h-4 w-4 mr-1 text-gray-500" />
                    Benutzername
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Mail className="h-4 w-4 mr-1 text-gray-500" />
                    E-Mail
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Shield className="h-4 w-4 mr-1 text-gray-500" />
                    Rolle
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  >
                    <option value="user">Benutzer</option>
                    <option value="agent">Agent</option>
                    <option value="jk_manager">JK Manager</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Building className="h-4 w-4 mr-1 text-gray-500" />
                    Abteilung
                  </label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  >
                    <option value="">Keine Abteilung</option>
                    <option value="AutoSchiess">Auto Schiess</option>
                    <option value="JK">JK</option>
                    <option value="Special">Special</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <ToggleLeft className="h-4 w-4 mr-1 text-gray-500" />
                    Status
                  </label>
                  <select
                    name="is_active"
                    value={formData.is_active === true ? 'true' : 'false'}
                    onChange={(e) => {
                      const newValue = e.target.value === 'true';
                      console.log('Select changed to:', e.target.value, 'converted to:', newValue);
                      setFormData(prev => {
                        const updated = {
                          ...prev,
                          is_active: newValue
                        };
                        console.log('Updated formData:', updated);
                        return updated;
                      });
                    }}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  >
                    <option value="true">Aktiv</option>
                    <option value="false">Inaktiv</option>
                  </select>
                </div>
              </div>
              
              {/* Password e 3CX */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700 border-b pb-2 flex items-center">
                  <Key className="h-5 w-5 mr-2 text-indigo-500" />
                  Sicherheit & Integration
                </h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Key className="h-4 w-4 mr-1 text-gray-500" />
                    Passwort
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Phone className="h-4 w-4 mr-1 text-gray-500" />
                    3CX-Operator ID
                  </label>
                  <input
                    type="text"
                    name="cx3_id"
                    value={formData.cx3_id}
                    onChange={handleInputChange}
                    placeholder="z.B. 063"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <UserCog className="h-4 w-4 mr-1 text-gray-500" />
                    3CX-Operator Name (optional)
                  </label>
                  <input
                    type="text"
                    name="cx3_name"
                    value={formData.cx3_name}
                    onChange={handleInputChange}
                    placeholder="z.B. Max Mustermann"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  />
                </div>
                
                <div>
                  <p className="text-xs sm:text-sm text-gray-500">
                    Das Passwort sollte mindestens 8 Zeichen lang sein und Buchstaben, Zahlen und Sonderzeichen enthalten.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 border-t pt-4 sm:pt-6">
              <button
                type="button"
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
                onClick={() => {
                  setError(''); // Reset error when closing
                  setShowAddModal(false);
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Abbrechen
              </button>
              <button
                type="button"
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
                onClick={handleAddUser}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Verarbeitung...
                  </>
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-1" />
                    Benutzer erstellen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 md:p-8 max-w-2xl w-full my-8 relative">
            <div className="sticky top-0 bg-white z-10 pb-2">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                  <UserCog className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-indigo-600" />
                  Benutzer bearbeiten
                </h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>
              
              {error && (
                <div className="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-500 p-3 sm:p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 gap-4 sm:gap-6">
              {/* Informazioni utente */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700 border-b pb-2 flex items-center">
                  <User className="h-5 w-5 mr-2 text-indigo-500" />
                  Benutzerinformationen
                </h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <UserCheck className="h-4 w-4 mr-1 text-gray-500" />
                    Benutzername
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    disabled
                    className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm py-2 px-3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Mail className="h-4 w-4 mr-1 text-gray-500" />
                    E-Mail
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm py-2 px-3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Shield className="h-4 w-4 mr-1 text-gray-500" />
                    Rolle
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  >
                    <option value="user">Benutzer</option>
                    <option value="agent">Agent</option>
                    <option value="jk_manager">JK Manager</option>
                    <option value="admin">Administrator</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Building className="h-4 w-4 mr-1 text-gray-500" />
                    Abteilung
                  </label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  >
                    <option value="">Keine Abteilung</option>
                    <option value="AutoSchiess">Auto Schiess</option>
                    <option value="JK">JK</option>
                    <option value="Special">Special</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <ToggleLeft className="h-4 w-4 mr-1 text-gray-500" />
                    Status
                  </label>
                  <select
                    name="is_active"
                    value={formData.is_active === true ? 'true' : 'false'}
                    onChange={(e) => {
                      const newValue = e.target.value === 'true';
                      console.log('Select changed to:', e.target.value, 'converted to:', newValue);
                      setFormData(prev => {
                        const updated = {
                          ...prev,
                          is_active: newValue
                        };
                        console.log('Updated formData:', updated);
                        return updated;
                      });
                    }}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  >
                    <option value="true">Aktiv</option>
                    <option value="false">Inaktiv</option>
                  </select>
                </div>
              </div>
              
              {/* Password e 3CX */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700 border-b pb-2 flex items-center">
                  <Key className="h-5 w-5 mr-2 text-indigo-500" />
                  Sicherheit & Integration
                </h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Key className="h-4 w-4 mr-1 text-gray-500" />
                    Neues Passwort
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <Phone className="h-4 w-4 mr-1 text-gray-500" />
                    3CX-Operator ID
                  </label>
                  <input
                    type="text"
                    name="cx3_id"
                    value={formData.cx3_id}
                    onChange={handleInputChange}
                    placeholder="z.B. 063"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <UserCog className="h-4 w-4 mr-1 text-gray-500" />
                    3CX-Operator Name (optional)
                  </label>
                  <input
                    type="text"
                    name="cx3_name"
                    value={formData.cx3_name}
                    onChange={handleInputChange}
                    placeholder="z.B. Max Mustermann"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 py-2 px-3"
                  />
                </div>
              </div>
            </div>
            
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 border-t pt-4 sm:pt-6">
              <button
                type="button"
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
                onClick={() => setShowEditModal(false)}
              >
                <X className="h-4 w-4 mr-1" />
                Abbrechen
              </button>
              <button
                type="button"
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
                onClick={handleUpdateUser}
              >
                <Check className="h-4 w-4 mr-1" />
                Änderungen speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Stats Modal */}
      {showStatsModal && userStats && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start justify-center z-50 p-4 sm:items-center overflow-y-auto">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full mt-16 sm:mt-0">
            <div className="sticky top-0 bg-white pb-3 mb-3 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                Statistiken für {userStats.username}
              </h3>
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-indigo-50 p-3 sm:p-4 rounded-md">
                <h4 className="font-medium text-indigo-700 mb-2 text-sm sm:text-base">Anrufaktivität</h4>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Gesamte Anrufe</p>
                    <p className="text-xl sm:text-2xl font-bold text-indigo-600">{userStats.total_calls}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Verkäufe</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">{userStats.sold_calls}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 p-3 sm:p-4 rounded-md">
                <h4 className="font-medium text-green-700 mb-2 text-sm sm:text-base">Erfolgsrate</h4>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Verkaufsrate</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">
                      {userStats.total_calls > 0 
                        ? ((userStats.sold_calls / userStats.total_calls) * 100).toFixed(1) + '%' 
                        : '0%'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Feedback Positiv</p>
                    <p className="text-xl sm:text-2xl font-bold text-indigo-600">
                      {userStats.positive_feedback_calls || 0}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 sm:p-4 rounded-md">
                <h4 className="font-medium text-blue-700 mb-2 text-sm sm:text-base">Aktivität Zeitraum</h4>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Letzter Anruf</p>
                    <p className="text-sm font-medium text-gray-800">
                      {userStats.last_call_date ? new Date(userStats.last_call_date).toLocaleDateString('de-DE') : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Erste Aktivität</p>
                    <p className="text-sm font-medium text-gray-800">
                      {userStats.first_call_date ? new Date(userStats.first_call_date).toLocaleDateString('de-DE') : '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-5 sm:mt-6 flex justify-end">
              <button
                type="button"
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => setShowStatsModal(false)}
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start justify-center z-50 p-4 sm:items-center overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full mt-16 sm:mt-0">
            <div className="flex items-start mb-4 sm:mb-6">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-base sm:text-lg font-medium text-red-600">Benutzer löschen</h3>
                <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-700">
                  Sind Sie sicher, dass Sie den Benutzer <strong>"{selectedUser.username}"</strong> löschen möchten?
                </p>
              </div>
            </div>
            
            <div className="bg-red-50 p-3 sm:p-4 rounded-md mb-4 sm:mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-xs sm:text-sm font-medium text-red-800">Achtung</h3>
                  <div className="mt-1 sm:mt-2 text-xs sm:text-sm text-red-700">
                    <p>Diese Aktion kann nicht rückgängig gemacht werden. Der Benutzer wird dauerhaft gelöscht.</p>
                    <p className="font-medium mt-1">Alle mit diesem Benutzer verbundenen Daten bleiben erhalten, werden aber nicht mehr diesem Benutzer zugeordnet.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0">
              <button
                type="button"
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
                onClick={() => setShowDeleteConfirmModal(false)}
              >
                <X className="h-4 w-4 mr-1" />
                Abbrechen
              </button>
              <button
                type="button"
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center justify-center"
                onClick={confirmDeleteUser}
                disabled={loading}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {loading ? 'Wird gelöscht...' : 'Benutzer löschen'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default UserManagement;